import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsRestaurant } from '../../hooks/useAccountType';
import Layout from '../Layout/Layout';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from '../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';

import {
  useGetScenes, useCreateScene, useDeleteScene, useActivateScene,
} from '../../queries/scenes';
import { useGetHardwareByRole } from '../../queries/hardware';
import { useCustomToast } from '../Contexts/ToastContext';
import FeatureLocked from '../SharedComponents/FeatureLocked';
import FormField from '../SharedComponents/Forms/FormField';
import EmptyState from '../SharedComponents/Forms/EmptyState';
import { useInHub } from '../../nav/useInHub';

const EMPTY_SCENE = { id: null, name: '', is_default: false, scene_actions: [] };
const newAction = (step) => ({ step, action_type: 'campaign_switch', payload: {} });

export default function ScenesPage() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const isRestaurant = useIsRestaurant();

  const ACTION_TYPES = [
    { label: t('hardware.scenes.form.campaign_switch'), value: 'campaign_switch' },
    { label: t('hardware.scenes.form.ptn_command'),     value: 'ptn_command' },
    { label: t('hardware.scenes.form.channel_switch'),  value: 'channel_switch' },
    { label: t('hardware.scenes.form.audio_control'),   value: 'audio_control' },
    { label: t('hardware.scenes.form.ir_command'),      value: 'ir_command' },
  ];

  const PTN_TYPE_OPTIONS = [
    { label: t('hardware.scenes.form.ptn_type_v'),  value: 'v'  },
    { label: t('hardware.scenes.form.ptn_type_a'),  value: 'a'  },
    { label: t('hardware.scenes.form.ptn_type_va'), value: 'va' },
  ];
  const { showToast } = useCustomToast();

  const [dialog, setDialog] = useState(false);
  const [form, setForm]     = useState(EMPTY_SCENE);

  const { data, isLoading, error } = useGetScenes();
  const scenes = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

  const createMut   = useCreateScene();
  const deleteMut   = useDeleteScene();
  const activateMut = useActivateScene();

  // Role-filtered hardware lists for the PTN command builder.
  const { data: encData } = useGetHardwareByRole('encoder');
  const { data: decData } = useGetHardwareByRole('decoder');
  const encoderRows = Array.isArray(encData?.data) ? encData.data : Array.isArray(encData) ? encData : [];
  const decoderRows = Array.isArray(decData?.data) ? decData.data : Array.isArray(decData) ? decData : [];

  const encoderOpts = encoderRows.map((h) => ({
    label: h.port_number ? `${h.name} (port ${h.port_number})` : h.name,
    value: h.id,
  }));

  // 402 = check.feature gate denied. Render inline upgrade panel.
  if (error?.response?.status === 402) {
    return (
      isRestaurant
        ? <FeatureLocked feature="hardware_control" message={error.response.data?.message} />
        : <Layout padding={false}><FeatureLocked feature="hardware_control" /></Layout>
    );
  }

  const openCreate = () => {
    setForm({ ...EMPTY_SCENE, scene_actions: [newAction(0)] });
    setDialog(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name ?? '',
      is_default: !!row.is_default,
      scene_actions: Array.isArray(row.scene_actions) && row.scene_actions.length
        ? row.scene_actions.map((a) => ({ ...a, payload: a.payload ?? {} }))
        : [newAction(0)],
    });
    setDialog(true);
  };

  const updateAction = (idx, patch) => {
    const next = [...form.scene_actions];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, scene_actions: next });
  };
  const updatePayload = (idx, key, value) => {
    const next = [...form.scene_actions];
    next[idx] = { ...next[idx], payload: { ...(next[idx].payload ?? {}), [key]: value } };
    setForm({ ...form, scene_actions: next });
  };
  const addAction = () =>
    setForm({ ...form, scene_actions: [...form.scene_actions, newAction(form.scene_actions.length)] });
  const removeAction = (idx) => {
    const next = form.scene_actions.filter((_, i) => i !== idx).map((a, i) => ({ ...a, step: i }));
    setForm({ ...form, scene_actions: next });
  };

  const submit = async () => {
    const payload = {
      name: form.name,
      is_default: form.is_default,
      scene_actions: form.scene_actions.map((a, i) => ({
        step: i,
        action_type: a.action_type,
        target_device_id: a.target_device_id ?? null,
        payload: a.payload ?? {},
      })),
    };
    try {
      if (form.id) {
        const axios = (await import('../../config/Axios')).default;
        await axios.put(`/scenes/${form.id}/update`, payload);
        showToast('success', 'Scenes', t('messages.updated'));
      } else {
        await createMut.mutateAsync(payload);
      }
      setDialog(false);
    } catch (e) {
      showToast('error', 'Scenes', e.response?.data?.message ?? t('messages.operation_failed'));
    }
  };

  const confirmDeleteRow = (row) =>
    confirmDialog({
      message: `${t('messages.confirm_delete')} (${row.name})`,
      header: t('buttons.confirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => deleteMut.mutate(row.id),
    });

  const actionsTemplate = (row) => (
    <div className="d-inline-flex gap-1">
      <Button
        size="small" icon={activateMut.isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
        label={t('hardware.scenes.activate')}
        onClick={() => activateMut.mutate(row.id)}
        style={{ background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none' }}
      />
      <Button text rounded size="small" icon="pi pi-pencil"
        tooltip={t('buttons.edit')} tooltipOptions={{ position: 'top' }}
        onClick={() => openEdit(row)} />
      <Button text rounded size="small" severity="danger" icon="pi pi-trash"
        tooltip={t('buttons.delete')} tooltipOptions={{ position: 'top' }}
        onClick={() => confirmDeleteRow(row)} />
    </div>
  );

  // Map common scene name keywords → emoji so the table header reads at a glance.
  const sceneIcon = (name = '') => {
    const n = String(name).toLowerCase();
    if (/(match|game|sport|football|kick)/.test(n))   return '⚽';
    if (/(cafe|coffee|chill|lounge)/.test(n))         return '☕';
    if (/(party|disco|club|dance)/.test(n))           return '🎉';
    if (/(birthday|cake)/.test(n))                    return '🎂';
    if (/(prayer|salah|adhan)/.test(n))               return '🕌';
    if (/(off|shutdown|close|sleep)/.test(n))         return '🌙';
    return '🎬';
  };

  // ── Per-action payload editor (renders different fields per action_type) ──

  const ActionEditor = ({ action, idx }) => {
    const payload = action.payload ?? {};

    return (
      <div className="border rounded p-3 mb-2" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong className="small">#{idx + 1}</strong>
          <Button text size="small" severity="danger" icon="pi pi-times"
            onClick={() => removeAction(idx)} tooltip={t('hardware.scenes.form.remove')} />
        </div>

        <div className="row g-2 mb-2">
          <div className="col-12">
            <label className="small fw-semibold">{t('hardware.scenes.form.action_type')}</label>
            <Dropdown
              value={action.action_type}
              options={ACTION_TYPES}
              onChange={(e) => updateAction(idx, { action_type: e.value, payload: {} })}
              className="w-100"
            />
          </div>
        </div>

        {action.action_type === 'campaign_switch' && (
          <div>
            <label className="small fw-semibold">{t('hardware.scenes.form.campaign_id')}</label>
            <InputText
              value={payload.campaign_id ?? ''}
              onChange={(e) => updatePayload(idx, 'campaign_id', e.target.value)}
              className="w-100"
              placeholder="UUID"
            />
          </div>
        )}

        {action.action_type === 'ptn_command' && (() => {
          // Resolve encoder.id (UI) → port_number (the >Switch input) +
          // parent_device_id (the Control Box — the cmd target hardware_id).
          const selEnc      = encoderRows.find((e) => e.id === payload.encoder_id);
          const encPort     = selEnc?.port_number;
          const controlBox  = selEnc?.parent_device_id;
          // Decoders are filtered to the same control box so we never build
          // a cross-box route by accident. 'all' is always available.
          const decoderOpts = [
            { label: t('hardware.scenes.form.ptn_output') + ' — all', value: 'all' },
            ...decoderRows
              .filter((d) => !controlBox || d.parent_device_id === controlBox)
              .map((d) => ({
                label: d.port_number ? `${d.name} (port ${d.port_number})` : d.name,
                value: d.id,
              })),
          ];
          const selDec     = decoderRows.find((d) => d.id === payload.decoder_id);
          const decPort    = payload.decoder_id === 'all' ? 'all' : selDec?.port_number;
          const cmdPreview = encPort && decPort
            ? `>Switch ${encPort} ${decPort} ${payload.type ?? 'va'}`
            : '— pick encoder + target —';

          // Whenever any of the 3 inputs change, rewrite payload.hardware_id +
          // payload.input + payload.output + payload.cmd so the service has
          // everything it needs without re-resolving.
          const sync = (next) => {
            const merged   = { ...payload, ...next };
            const encRow   = encoderRows.find((e) => e.id === merged.encoder_id);
            const decRow   = decoderRows.find((d) => d.id === merged.decoder_id);
            const cmd      = encRow?.port_number && (merged.decoder_id === 'all' || decRow?.port_number)
              ? `>Switch ${encRow.port_number} ${merged.decoder_id === 'all' ? 'all' : decRow.port_number} ${merged.type ?? 'va'}`
              : null;
            updatePayload(idx, 'encoder_id', merged.encoder_id ?? null);
            updatePayload(idx, 'decoder_id', merged.decoder_id ?? null);
            updatePayload(idx, 'type',       merged.type ?? 'va');
            updatePayload(idx, 'hardware_id', encRow?.parent_device_id ?? null);
            updatePayload(idx, 'input',  encRow?.port_number ?? null);
            updatePayload(idx, 'output', merged.decoder_id === 'all' ? 'all' : (decRow?.port_number ?? null));
            updatePayload(idx, 'cmd',    cmd);
          };

          return (
            <div className="row g-2">
              <div className="col-12">
                <label className="small fw-semibold">{t('hardware.scenes.form.ptn_input')}</label>
                <Dropdown
                  value={payload.encoder_id ?? null}
                  options={encoderOpts}
                  placeholder="—"
                  onChange={(e) => sync({ encoder_id: e.value })}
                  className="w-100"
                />
              </div>
              <div className="col-8">
                <label className="small fw-semibold">{t('hardware.scenes.form.ptn_output')}</label>
                <Dropdown
                  value={payload.decoder_id ?? 'all'}
                  options={decoderOpts}
                  onChange={(e) => sync({ decoder_id: e.value })}
                  className="w-100"
                />
              </div>
              <div className="col-4">
                <label className="small fw-semibold">{t('hardware.scenes.form.ptn_type')}</label>
                <Dropdown
                  value={payload.type ?? 'va'}
                  options={PTN_TYPE_OPTIONS}
                  onChange={(e) => sync({ type: e.value })}
                  className="w-100"
                />
              </div>
              <div className="col-12">
                <small className="text-muted">cmd: <code>{cmdPreview}</code></small>
              </div>
            </div>
          );
        })()}

        {action.action_type === 'channel_switch' && (
          <div className="row g-2">
            <div className="col-12">
              <label className="small fw-semibold">{t('hardware.scenes.form.channel_id')}</label>
              <InputText
                value={payload.channel_id ?? ''}
                onChange={(e) => updatePayload(idx, 'channel_id', e.target.value)}
                placeholder="channel UUID"
                className="w-100"
              />
            </div>
            <div className="col-12">
              <label className="small fw-semibold">{t('hardware.scenes.form.screens')}</label>
              <InputText
                value={(payload.screens ?? []).join(', ')}
                onChange={(e) =>
                  updatePayload(idx, 'screens', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))
                }
                placeholder="screen UUIDs (comma-separated)"
                className="w-100"
              />
            </div>
          </div>
        )}

        {action.action_type === 'audio_control' && (
          <div className="row g-2 align-items-center">
            <div className="col-8">
              <label className="small fw-semibold">{t('hardware.scenes.form.decoder_id')}</label>
              <InputText
                value={payload.decoder_id ?? ''}
                onChange={(e) => updatePayload(idx, 'decoder_id', e.target.value)}
                placeholder="hardware UUID"
                className="w-100"
              />
            </div>
            <div className="col-4 d-flex align-items-center gap-2 pt-3">
              <InputSwitch
                checked={!!payload.mute}
                onChange={(e) => updatePayload(idx, 'mute', e.value)}
              />
              <label className="small fw-semibold mb-0">{t('hardware.scenes.form.mute')}</label>
            </div>
          </div>
        )}

        {action.action_type === 'ir_command' && (
          <div>
            <label className="small fw-semibold">{t('hardware.scenes.form.action_type')}: IR</label>
            <InputText
              value={payload.ir_cmd ?? ''}
              onChange={(e) => updatePayload(idx, 'ir_cmd', e.target.value)}
              placeholder="sendir,..."
              className="w-100"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-3">
      <ConfirmDialog />
      <div className="pp-page-header">
        {!inHub && (
          <h3 className="pp-page-title">
            <i className="pi pi-sliders-h" />
            {t('hardware.scenes.title')}
          </h3>
        )}
        <Button icon="pi pi-plus" label={t('hardware.scenes.add')} onClick={openCreate} />
      </div>

      <div className="pp-card">
        {isLoading ? (
          <div className="p-3">
            <Skeleton height="2rem" className="mb-2" />
            <Skeleton height="2rem" className="mb-2" />
            <Skeleton height="2rem" />
          </div>
        ) : scenes.length === 0 ? (
          <EmptyState
            icon="pi-sliders-h"
            title={t('hardware.scenes.empty_title')}
            description={t('hardware.scenes.empty_desc')}
            cta={{ label: t('hardware.scenes.add'), onClick: openCreate }}
          />
        ) : (
          <DataTable value={scenes} paginator rows={10} responsiveLayout="scroll" stripedRows
                     emptyMessage={t('table.no_data')}>
            <Column field="name" header={t('hardware.scenes.table.name')} sortable
              body={(r) => (
                <span className="d-inline-flex align-items-center gap-2">
                  <span style={{ fontSize: '1.1rem' }}>{sceneIcon(r.name)}</span>
                  <strong>{r.name}</strong>
                </span>
              )} />
            <Column field="scene_actions_count"
              header={t('hardware.scenes.table.actions_count')}
              body={(r) => (
                <span className="pp-status pp-status-neutral">
                  {r.scene_actions_count ?? r.scene_actions?.length ?? 0}
                </span>
              )}
            />
            <Column field="is_default"
              header={t('hardware.scenes.table.default')}
              body={(r) => r.is_default
                ? <span className="pp-status pp-status-online">{t('hardware.scenes.is_default')}</span>
                : <span className="text-muted">—</span>}
            />
            <Column header={t('hardware.scenes.table.actions')} body={actionsTemplate} style={{ width: 240 }} />
          </DataTable>
        )}
      </div>

      <Dialog
        header={
          <span><i className="pi pi-sliders-h me-2" />
            {form.id ? t('hardware.scenes.edit') : t('hardware.scenes.add')}
          </span>
        }
        visible={dialog}
        onHide={() => setDialog(false)}
        style={{ width: 'min(95vw, 720px)' }}
        modal
      >
        <div className="d-flex flex-column" style={{ gap: '1.25rem' }}>
          <FormField icon="pi-tag" label={t('hardware.scenes.form.name')} required>
            <InputText
              value={form.name}
              placeholder={t('hardware.scenes.form.name_ph')}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </FormField>

          <div className="pp-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.75rem' }}>
            <InputSwitch
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.value })}
            />
            <label className="pp-field-label mb-0" style={{ textTransform: 'none', fontSize: '0.88rem' }}>
              <i className="pi pi-star" />
              {t('hardware.scenes.form.is_default')}
            </label>
          </div>

          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small text-uppercase" style={{ color: 'var(--pp-text-secondary)', letterSpacing: '0.04em' }}>
                <i className="pi pi-bolt me-1" style={{ color: 'var(--pp-primary)' }} />
                {t('hardware.scenes.form.actions_title')}
              </strong>
              <Button size="small" outlined icon="pi pi-plus" label={t('hardware.scenes.form.add_action')} onClick={addAction} />
            </div>

            {form.scene_actions.map((a, idx) => (
              <ActionEditor key={idx} action={a} idx={idx} />
            ))}
          </div>

          <div className="pp-dialog-footer">
            <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={() => setDialog(false)} />
            <Button
              icon={form.id ? 'pi pi-check' : 'pi pi-plus'}
              label={form.id ? t('buttons.update') : t('buttons.create')}
              loading={createMut.isLoading} onClick={submit}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
